// src/encounter/eprescription-preview-dialog.tsx
// The ePrescription preview — Flutter `eprescription_popup.dart` (`EPrescriptionPopUp`), opened
// from the wrap-up header's eRx preview action (wrap_up_billing_screen.dart:320-346).
//
// REBUILT 2026-09-07 to the full document. What stood here was a compact summary — an identity
// grid, two comma lists and a 6-column table — against a source that renders a printable
// prescription: letterhead, ID line, patient details in three columns, diagnosis with codes and
// Primary/Secondary, a 7-column zebra table, signature rules and the community panel. Ahmed,
// 2026-09-07: "the content in the modal [is] not even close [to] the one we have locally."
//
// THE ROOT CAUSE was not styling. `EPrescriptionPreview.patient` did not exist on the wire — the
// key is `patientDetails` — so `{patient && …}` was always false and the whole identity block
// rendered nothing. `practice`, `organization` and `sponsor` were on the wire too and simply
// untyped. All three are fixed in services/use-eprescription.ts; see its header note for the
// captured response shape.
//
// Flutter's document order for `previewOrderType == null` (EPP:136-183), which is what the
// wrap-up passes (:337):
//   logo → ID line + clinic letterhead → 42 → PATIENT DETAILS → 16 → rule → 26 → DIAGNOSIS
//   → 16 → rule → 32 → PRESCRIPTIONS → 32 → signatures → 30 → community panel → 25 → logo
//
// ALLERGIES IS DELIBERATELY ABSENT. Flutter gates it on
// `previewOrderType == PreviewOrderEnum.Medications` (EPP:150-154); the wrap-up passes null, so
// the live document has no allergies section — confirmed against the deployed app. The previous
// version rendered it unconditionally.
//
// COLOURS: Signal DS tokens only. Flutter's palette on this surface is scripsTitleBlue, its cyan,
// scripsIconsGrey and scripsSubText — the first THREE are in the DS REJECTED_COLORS list and can
// never be reproduced (scripsSubText is not rejected; it simply has no better seat than the hint
// token). The freeze doctrine's own substitution rule
// (parity-baseline/2026-07-20-encounter/FREEZE-MANIFEST.json → doctrine.substitutions_allowed)
// maps the cyan to var(--color-brand-primary); the title blue takes the same seat, matching the
// mapping correction made to wrap-up-invoice-section.tsx on the same pass.
//
// KNOWN GAPS, noted rather than faked:
//   · the Scrips wordmark at the top and bottom of the sheet (EPP:138-145, :175-182) needs
//     `assets/ic_logo.png`; neither this repo nor the DS ships a Scrips logo asset.
//   · `practiceStampUrl` / `doctorSignatureUrl` are not on the wire, so the signature block
//     renders its empty 60px slot + rule + label — which is what Flutter itself draws when the
//     URL is blank (signature_widget.dart:18), and what the deployed document shows.
//   · Flutter's Print rasterises the document to a 597x1430 PDF (EPP:99-104). On the web the
//     equivalent is the browser's own print pipeline, which is exactly the substitution
//     growth-chart-print-dialog.tsx:7-11 already makes for the other print icon in this app — so
//     Print is WIRED here, not stubbed. The `@media print` rule lives with the dialog because it
//     must only apply while the dialog is mounted.
import type { ReactNode } from 'react';
import { Modal } from '@scripsteam/scrips-signal-ds';
import type {
  EPrescriptionPreview,
  EPrescriptionCoding,
  EPrescriptionLine,
} from './services/use-eprescription';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';

export interface EPrescriptionPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  preview?: EPrescriptionPreview;
  loading?: boolean;
  error?: boolean;
  /** Override the print handler. Absent → the browser print pipeline, scoped to the sheet. */
  onPrint?: () => void;
}

/* ── shell ─────────────────────────────────────────────────────────────────────
   Flutter: SimpleDialog, radius 13, background lightGrey, content
   min(1100, 90vw) x 80vh (popup_utils.dart:415-427). The DS Modal expresses all of it with props:
   `size="document"` is the min(1100px, 90vw) width step, `maxHeight="print"` the 80vh cap,
   `variant="bleed"` the zero-padding body, `scrollBody` + `bodyLayout="column"` a clipped flex
   column whose children lay out as a column, `titleHidden` keeps "Prescription" as the accessible
   name while Flutter's own centred header row draws the visible one, and `printable` sends ONLY
   this dialog's body to paper (the header row opts out with `print:hidden`).
   Flutter puts the Close/Print row OUTSIDE the scroller — `Column[header, Expanded(body)]`
   (EPP:62-70) — so the row is a fixed-height column child and only the SHEET scrolls. */
const HEADER_ROW = 'flex items-center px-4 mt-2 shrink-0 print:hidden';
const HEADER_BTN =
  'bg-transparent border-0 p-0 cursor-pointer text-size-title font-semibold text-brand-primary';
const HEADER_TITLE = 'text-size-title font-bold text-text-primary';

/** The white document sheet — Flutter margin 22/22/30/0, padding 32h/20v (EPP:118-131). It is the
 *  one scroller (EPP:62-70); on paper it flows unclipped. */
const SHEET =
  'bg-surface-card mt-7.5 mx-5.5 mb-0 py-5 px-8 overflow-y-auto flex-1 min-h-0 print:m-0 print:overflow-visible';

const DOC = 'flex flex-col';
/** boldLabelTextStyle(24, scripsTitleBlueColor) — EPP:242. */
const DOC_TITLE = 'm-0 text-size-lg font-bold text-brand-primary';
/** boldLabelTextStyle(12, scripsTitleBlueColor), uppercase — EPP:260-266.
 *  NOTE: this is a LOCAL constant, unrelated to `SECTION_LABEL_CLASS` in ./pre-encounter-parts —
 *  this file never imported that one (different color/tracking); see the conversion report. */
const SECTION_LABEL =
  'm-0 text-size-eyebrow font-bold tracking-caps uppercase text-brand-primary';
const RULE = 'h-px bg-border-default border-0 m-0';
const MUTED = 'm-0 text-size-sm text-text-secondary';

/* ── letterhead (ClinicDetailsWidget) ───────────────────────────────────────── */
/* The 192px cap belongs to the practice name/address/phone block ONLY — Flutter puts it on an
   inner `SizedBox(width: 192)` (clinic_details_widget.dart:64), while the org name and the
   thank-you line sit on the outer Column and may run wider. Capping the whole column wrapped
   "Thank you for choosing Scrips Organization" onto two lines; the deployed document keeps it
   on one. */
const CLINIC = 'flex flex-col items-end text-right shrink-0';
const CLINIC_ADDRESS_BLOCK = 'w-48';
const CLINIC_ORG = 'text-size-sm font-bold text-text-primary';
const CLINIC_LINE = 'text-sm text-text-primary';
const CLINIC_PHONE = 'text-sm text-brand-primary';

/* ── patient details (InfoWidget) ───────────────────────────────────────────── */
const INFO_COLS = 'grid grid-cols-3 items-start';
/** Flutter's Table is 115:148 proportional with a 12+12 gutter (info_widget.dart:28-44) — two
 *  zero-basis flex cells growing 115 : 148 are the same proportional split. */
const INFO_ROW = 'grid grid-cols-erx-info gap-x-6 pb-2';
const INFO_LABEL = 'text-size-eyebrow font-medium text-text-hint text-right';
const INFO_VALUE = 'text-size-eyebrow text-text-primary leading-body';

/* ── diagnosis (diagnosis_list_widget) ──────────────────────────────────────── */
/** auto-fit minmax(200px, 1fr) tracks (the DS `grid-cols-diagnoses` template, shared with the
 *  diagnosis picker): empty tracks collapse, so an odd last row keeps its column width. */
const DX_WRAP = 'grid grid-cols-diagnoses gap-y-3';
const DX_ITEM = 'flex gap-4 pr-4 min-w-0';
const DX_INDEX = 'text-size-eyebrow font-bold text-text-primary shrink-0';
const DX_TEXT = 'flex flex-col gap-2 min-w-0';
const DX_TITLE = 'text-size-sm font-bold text-text-primary';
const DX_DESC = 'text-sm text-text-hint whitespace-pre-line';

/* ── prescriptions table ────────────────────────────────────────────────────── */
const TABLE = 'w-full border-collapse';
/** headerText(...) — boldLabelTextStyle(12, scripsIconsGreyColor), uppercase (EPP:519-532). */
const TH =
  'text-left p-2 text-size-eyebrow font-bold uppercase text-text-hint whitespace-nowrap';
/** bodyText() — normalLabelTextStyle(13, black, letterSpacing -0.5) (EPP:534-548). */
const TD = 'p-2 text-sm tracking-document text-text-primary align-top';
const TD_SUB = 'block mt-1.25 text-size-xs tracking-document';

/* ── signatures ─────────────────────────────────────────────────────────────── */
const SIGS = 'flex gap-35.5';
const SIG = 'w-41.25';
const SIG_SLOT = 'h-15';
const SIG_RULE = 'h-px bg-text-primary border-0 m-0';
const SIG_LABEL = 'mt-3 text-sm text-text-primary';

/* ── community panel ────────────────────────────────────────────────────────── */
const COMMUNITY =
  'bg-grey-200 rounded-card pt-6 pr-7.75 pb-6.5 pl-6';
const COMMUNITY_TITLE = 'm-0 text-size-title font-bold text-brand-primary';
const COMMUNITY_BODY =
  'mt-4 text-xs text-text-primary whitespace-pre-line';

/** Flutter's `-` fallback for the identity fields (EPP:657-697). */
function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div data-slot="erx-info-row" className={INFO_ROW}>
      <div data-slot="erx-info-label" className={INFO_LABEL}>{label}</div>
      <div data-slot="erx-info-value" className={INFO_VALUE}>{value?.trim() ? value : '-'}</div>
    </div>
  );
}

function InfoColumn({ children }: { children: ReactNode }) {
  return <div>{children}</div>;
}

/**
 * Flutter `codeAndType()` — "{code} •  {system}", the system defaulting to ICD-10
 * (patient_assessment_model.dart:906-916) — over a Primary/Secondary line
 * (diagnosis_list_widget.dart:89-92).
 */
function dxDescription(d: EPrescriptionCoding): string {
  const codeLine = [d.code?.trim(), d.system?.trim() || 'ICD-10'].filter(Boolean).join(' • ');
  return `${codeLine}\n${d.type?.toLowerCase() === 'primary' ? 'Primary' : 'Secondary'}`;
}

/**
 * DURATION's second line — `"${scDateFormat(startDate)}-${scDateFormat(endDate)}"` (EPP:459-467).
 *
 * FORMATTED, and that is settled by OBSERVATION, not by reading `scDateFormat`. A review pass
 * argued for passing the string through, reasoning from utils.dart:176-184:
 *     if (value == null) return ""; else if (value is String) return value;
 *     else return DateFormat('MMM dd, yyyy').format(value);
 * i.e. "the wire sends strings, so Flutter prints them raw". **The deployed app disproves that** —
 * its DURATION cell reads `Sep 07, 2025-Sep 08, 2025`, not `07/09/2025-08/09/2025` (captured
 * 2026-09-07, _session-cache/wrapup-parity-2026-09-07/erx/deployed-erx-bottom.jpg). The model
 * field is therefore a real `DateTime` by the time it reaches this call, and the third branch runs.
 *
 * The same capture settles the day/month order, which cannot be inferred from `07/09/2026` alone:
 * the deployed encounter is dated **September 7** and renders **Sep 07**, so the wire is dd/MM/yyyy.
 * Anything that does not match that shape falls through unchanged rather than being guessed at.
 */
function dateRange(l: EPrescriptionLine): string | null {
  const fmt = (s?: string | null) => {
    const raw = s?.trim();
    if (!raw) return '';
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
    if (!m) return raw;
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return raw;
    const d = new Date(year, month - 1, day);
    // Reject a rolled-over date (e.g. 31/02) rather than printing a silently shifted one.
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return raw;
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };
  const a = fmt(l.startDate);
  const b = fmt(l.endDate);
  return a || b ? `${a}-${b}` : null;
}

export function EPrescriptionPreviewDialog({
  open,
  onClose,
  preview,
  loading,
  error,
  onPrint,
}: EPrescriptionPreviewDialogProps) {
  const patient = preview?.patientDetails;
  const practice = preview?.practice;
  const org = preview?.organization;
  const sponsor = preview?.sponsor;
  const lines = preview?.prescriptions ?? [];
  const diagnoses = preview?.diagnoses ?? [];

  const reference = preview?.ePrescriptionReference ?? preview?.ePerscription?.ePrescriptionReference;
  // EPP:240 — "ePrescription ID #<id>" when generated, else "ePrescription ID  / Not generated".
  const idLine = `ePrescription ID ${reference?.trim() ? `#${reference.trim()}` : ' / Not generated'}`;

  const handlePrint = onPrint ?? (() => window.print());

  const sponsorType = sponsor?.sponsorType?.trim().toLowerCase();
  const isInsurance = sponsorType === 'insurance';
  // An ABSENT sponsorType is NOT self-pay — Flutter's `if/else if/else` chain sends it to the
  // final `else` (Sponsor + Plan), so folding null into self-pay would drop a row the source shows.
  const isSelfPay = !!sponsorType && sponsorType.replace(/[^a-z]/g, '') === 'selfpay';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Prescription"
      titleHidden
      size="document"
      maxHeight="print"
      variant="bleed"
      scrollBody
      bodyLayout="column"
      printable
      className="bg-grey-200"
    >
      {/* Flutter's own header row: Close · centred title · Print (EPP:74-114). */}
      <div data-slot="erx-header" className={HEADER_ROW}>
        <button type="button" data-slot="erx-header-btn" className={HEADER_BTN} onClick={onClose}>
          Close
        </button>
        <span className="flex-1" />
        <span data-slot="erx-header-title" className={HEADER_TITLE}>Prescription</span>
        <span className="flex-1" />
        <button type="button" data-slot="erx-header-btn" className={HEADER_BTN} onClick={handlePrint} aria-label="Print">
          Print
        </button>
      </div>

      <div id="erx-print-sheet" data-slot="erx-sheet" className={SHEET}>
        {loading ? (
          <QuietLoading label="Loading prescription…" />
        ) : error ? (
          <p data-slot="erx-error" className="m-0 text-size-sm text-status-error">
            Couldn’t load the prescription. Close and try again.
          </p>
        ) : (
          <div className={DOC}>
            {/* ID line + clinic letterhead (EPP:231-253). */}
            <div data-slot="erx-top" className="flex items-start gap-10.5">
              <h3 data-slot="erx-title" className={`${DOC_TITLE} flex-1`}>{idLine}</h3>
              <div className={CLINIC}>
                {org?.logoUrl?.trim() ? (
                  <img src={org.logoUrl} alt="" className="h-13 max-w-50 object-contain" />
                ) : null}
                <div data-slot="erx-clinic-org" className={`${CLINIC_ORG} mt-2`}>{org?.name?.trim() ? org.name : '–'}</div>
                <div data-slot="erx-clinic-block" className={CLINIC_ADDRESS_BLOCK}>
                  <div data-slot="erx-clinic-line" className={`${CLINIC_LINE} mt-1`}>{practice?.name?.trim() ? practice.name : '-'}</div>
                  <div data-slot="erx-clinic-line" className={`${CLINIC_LINE} mt-1`}>{practice?.address?.trim() ? practice.address : '-'}</div>
                  {practice?.phone?.trim() ? <div data-slot="erx-clinic-phone" className={`${CLINIC_PHONE} mt-1`}>{practice.phone}</div> : null}
                </div>
                <div data-slot="erx-thanks" className={`${CLINIC_LINE} mt-4`}>
                  Thank you for choosing {org?.name?.trim() ? org.name : '–'}
                </div>
              </div>
            </div>

            {/* PATIENT DETAILS (EPP:256-275) — three columns (EPP:648-701). */}
            <section aria-label="Patient details" className="mt-10.5">
              <p className={SECTION_LABEL}>Patient Details</p>
              <div className={`${INFO_COLS} mt-4`}>
                <InfoColumn>
                  <InfoRow label="Name" value={patient?.name} />
                  <InfoRow label="DOB" value={patient?.dob} />
                  {/* Flutter renders `gender.capitalize.substring(0, 1)` — the FIRST LETTER only
                      (EPP:658). "Female" shows as "F". */}
                  <InfoRow label="Gender" value={patient?.gender?.trim().charAt(0).toUpperCase()} />
                  <InfoRow label="Height/Weight" value={patient?.heightWeight} />
                  <InfoRow label="MRN" value={patient?.mrn} />
                </InfoColumn>
                <InfoColumn>
                  {/* EPP:670-686, field-for-field. The two value bindings are NOT the obvious
                      ones: Flutter puts `sponsorType` under "Sponsor" and `sponsorName` under
                      "Plan" — it never reads `sponsor.plan` here at all. An earlier pass bound
                      Sponsor→sponsorName and Plan→plan, which the test fixture could not catch
                      because it set name and type to the same string.
                      Three-way, not two: Self-pay → Sponsor only; Insurance → all four; anything
                      ELSE, INCLUDING an absent sponsorType → Sponsor + Plan. */}
                  <InfoRow label="Sponsor" value={sponsor?.sponsorType} />
                  {!isSelfPay && <InfoRow label="Plan" value={sponsor?.sponsorName} />}
                  {isInsurance && <InfoRow label="TPA" value={sponsor?.tpa} />}
                  {isInsurance && <InfoRow label="Member ID" value={sponsor?.policyNo} />}
                </InfoColumn>
                <InfoColumn>
                  <InfoRow label="Provider" value={patient?.provider} />
                  <InfoRow label="Encounter Date" value={patient?.encounterDate} />
                </InfoColumn>
              </div>
            </section>

            <hr data-slot="erx-rule" className={`${RULE} mt-4`} />

            {/* DIAGNOSIS (EPP:158, diagnosis_list_widget.dart:21-64). */}
            <section aria-label="Diagnosis" className="mt-6.5">
              <p className={SECTION_LABEL}>Diagnosis</p>
              {diagnoses.length === 0 ? (
                <p data-slot="erx-muted" className={`${MUTED} mt-4`}>No diagnosis available</p>
              ) : (
                <div data-slot="erx-dx-wrap" className={`${DX_WRAP} mt-4`}>
                  {diagnoses.map((d, i) => (
                    <div key={`${d.code ?? d.display ?? 'dx'}-${i}`} data-slot="erx-dx-item" className={DX_ITEM} title={d.display ?? undefined}>
                      <span data-slot="erx-dx-index" className={DX_INDEX}>{i + 1}</span>
                      <span data-slot="erx-dx-text" className={DX_TEXT}>
                        <span data-slot="erx-dx-title" className={DX_TITLE}>{d.display}</span>
                        <span data-slot="erx-dx-desc" className={DX_DESC}>{dxDescription(d)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <hr data-slot="erx-rule" className={`${RULE} mt-4`} />

            {/* PRESCRIPTIONS (EPP:288-325) — Flutter's 7 columns, zebra on odd 1-based rows. */}
            <section aria-label="Prescriptions" className="mt-8">
              <p className={SECTION_LABEL}>Prescriptions</p>
              {lines.length === 0 ? (
                <p data-slot="erx-muted" className={`${MUTED} mt-4`}>No prescriptions available</p>
              ) : (
                <table data-slot="erx-table" className={`${TABLE} mt-4`}>
                  <caption className="sr-only">Prescribed medications</caption>
                  <thead>
                    <tr>
                      <th data-slot="erx-th" className={`${TH} w-6`} scope="col">#</th>
                      <th data-slot="erx-th" className={`${TH} w-75`} scope="col">Name</th>
                      <th data-slot="erx-th" className={`${TH} w-12`} scope="col">Dose</th>
                      <th data-slot="erx-th" className={`${TH} w-21`} scope="col">Frequency</th>
                      <th data-slot="erx-th" className={`${TH} w-32.5`} scope="col">Duration</th>
                      <th data-slot="erx-th" className={`${TH} w-21`} scope="col">Dispense</th>
                      <th data-slot="erx-th" className={`${TH} w-16`} scope="col">Route</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const index = i + 1;
                      // EPP:435-437 — odd 1-based rows carry the lightGrey fill at radius 13.
                      const zebraClass = index % 2 === 1 ? 'bg-grey-200' : '';
                      const range = dateRange(l);
                      return (
                        <tr key={l.id ?? `${l.name}-${i}`} className={zebraClass}>
                          <td data-slot="erx-td" className={TD}>{index}</td>
                          <td data-slot="erx-td" className={TD}>
                            {[l.name?.trim(), l.code?.trim()].filter(Boolean).join(' - ')}
                            {l.notes?.trim() ? <span data-slot="erx-sub" className={TD_SUB}>{l.notes}</span> : null}
                          </td>
                          <td data-slot="erx-td" className={TD}>{l.dose ?? '–'}</td>
                          <td data-slot="erx-td" className={TD}>{l.frequency ?? '–'}</td>
                          <td data-slot="erx-td" className={TD}>
                            {l.duration ?? '–'}
                            {range ? <span data-slot="erx-sub" className={TD_SUB}>{range}</span> : null}
                          </td>
                          <td data-slot="erx-td" className={TD}>{l.dispense ?? '–'}</td>
                          <td data-slot="erx-td" className={TD}>{l.route ?? '–'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>

            {/* SIGNATURES (EPP:550-595). No stamp/signature URL on the wire — Flutter draws the
                empty slot above the rule in exactly that case (signature_widget.dart:18). */}
            <div data-slot="erx-sigs" className={`${SIGS} mt-8`}>
              <div data-slot="erx-sig" className={SIG}>
                <div data-slot="erx-sig-slot" className={SIG_SLOT} />
                <hr data-slot="erx-sig-rule" className={SIG_RULE} />
                <div data-slot="erx-sig-label" className={SIG_LABEL}>Provider&apos;s Stamp</div>
              </div>
              <div data-slot="erx-sig" className={SIG}>
                <div data-slot="erx-sig-slot" className={SIG_SLOT} />
                <hr data-slot="erx-sig-rule" className={SIG_RULE} />
                <div data-slot="erx-sig-label" className={SIG_LABEL}>Provider&apos;s signature</div>
              </div>
            </div>

            {/* Community panel (scrips_info_with_app_download_widget.dart:18-68). */}
            <div data-slot="erx-community" className={`${COMMUNITY} mt-7.5`}>
              <p data-slot="erx-community-title" className={COMMUNITY_TITLE}>Be Part of Scrips Community</p>
              <p data-slot="erx-community-body" className={COMMUNITY_BODY}>
                {'Access your Invoice and other medical documents in your Scrips account.\n' +
                  'We value your health beyond clinical visits. There is so much our community based 360 degrees of care can offer you.'}
              </p>
              <hr data-slot="erx-rule" className={`${RULE} mt-4`} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
